<template>
  <div>
    <h4 v-if="!embedded" class="table-page-header">Quarterly Report</h4>

    <!-- Section 1: Generation Form -->
    <div class="filters-container" v-if="!embedded">
      <div class="filter-row">
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedClientId"
            :options="clientOptions"
            placeholder="Select DSPD Client"
            :searchable="true"
            :clearable="true"
            class="compact-select"
            @input="onClientChange"
          />
        </div>

        <div class="filter-group-compact date-group">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="-"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            class="compact-date-picker"
            @change="onDateChange"
          />
        </div>
      </div>

      <div class="filter-row" style="margin-top: 8px">
        <div class="quarter-buttons">
          <el-button
            v-for="q in quarterButtons"
            :key="q.label"
            size="small"
            :type="isActiveQuarter(q) ? 'primary' : 'default'"
            @click="setQuarter(q)"
          >
            {{ q.label }}
          </el-button>
        </div>
      </div>

      <div v-if="serviceCodes.length > 0" class="filter-row" style="margin-top: 8px">
        <div class="filter-group-compact">
          <AIVSelect
            v-model="selectedServiceCodes"
            :options="serviceCodeOptions"
            placeholder="Select Service Codes"
            :searchable="false"
            :clearable="true"
            :multiSelect="true"
            class="compact-select"
          />
        </div>
        <div class="filter-group-compact">
          <el-button
            type="success"
            @click="generateReport"
            :loading="generating"
            :disabled="!canGenerate"
          >
            <i class="fa fa-magic" style="margin-right: 6px"></i>
            Generate Report
          </el-button>
        </div>
      </div>

      <div class="filter-row" style="margin-top: 8px">
        <div class="filter-group-compact">
          <el-button
            type="info"
            @click="triggerUploadFile"
            :loading="uploading"
            :disabled="!canUpload"
          >
            <i class="fa fa-upload" style="margin-right: 6px"></i>
            Upload Report PDF
          </el-button>
          <input
            ref="uploadInput"
            type="file"
            accept=".pdf"
            style="display: none"
            @change="handleFileUpload"
          />
        </div>
      </div>

      <div v-if="codeWarning" class="code-warning">
        <i class="fa fa-exclamation-triangle"></i> {{ codeWarning }}
      </div>
    </div>

    <!-- Embedded mode: hidden file input + code warning -->
    <input
      v-if="embedded"
      ref="uploadInput"
      type="file"
      accept=".pdf"
      style="display: none"
      @change="handleFileUpload"
    />
    <div v-if="embedded && codeWarning" class="code-warning" style="margin: 8px 0">
      <i class="fa fa-exclamation-triangle"></i> {{ codeWarning }}
    </div>

    <!-- Section 2: Report Preview/Editor -->
    <div v-if="report" class="report-container">
      <div class="report-header-card">
        <div class="report-header-grid">
          <div class="header-field">
            <span class="header-label">Client:</span>
            <span class="header-value">{{ clientDisplayName }}</span>
          </div>
          <div class="header-field">
            <span class="header-label">Quarter:</span>
            <span class="header-value">{{ report.start_date }} to {{ report.end_date }}</span>
          </div>
          <div class="header-field">
            <span class="header-label">Created By:</span>
            <span class="header-value">{{ creatorDisplayName }}</span>
          </div>
          <div class="header-field">
            <span class="header-label">Service Codes:</span>
            <span class="header-value">{{ displayServiceCodes }}</span>
          </div>
          <div class="header-field">
            <span class="header-label">Status:</span>
            <span :class="['status-badge', report.status]">{{ report.status }}</span>
            <span v-if="report.uploaded_file_path" class="status-badge uploaded">uploaded</span>
          </div>
        </div>
      </div>

      <!-- Uploaded report notice -->
      <div v-if="report.uploaded_file_path" class="report-section">
        <p style="color: #b0b0b0; margin: 0;">
          <i class="fa fa-file-pdf" style="margin-right: 6px; color: #409eff;"></i>
          This report was uploaded as a PDF. Use the buttons below to download or email it.
        </p>
      </div>

      <!-- Editable sections (only for generated reports) -->
      <template v-if="!report.uploaded_file_path">
        <!-- Goals -->
        <div class="report-section">
          <h5 class="section-title">Client Goals</h5>
          <el-input
            v-model="report.goals_text"
            type="textarea"
            :rows="4"
            placeholder="Client goals..."
          />
        </div>

        <!-- Supports -->
        <div class="report-section">
          <h5 class="section-title">Client Supports</h5>
          <el-input
            v-model="report.supports_text"
            type="textarea"
            :rows="3"
            placeholder="Client supports..."
          />
        </div>

        <!-- Paragraph One -->
        <div class="report-section">
          <h5 class="section-title">Goals, Progress, and Staff Support</h5>
          <el-input
            v-model="report.paragraph_one"
            type="textarea"
            :rows="8"
            placeholder="AI-generated narrative about goals, progress, and staff support..."
          />
          <div class="char-count">{{ (report.paragraph_one || '').length }} characters</div>
        </div>

        <!-- Paragraph Two -->
        <div class="report-section">
          <h5 class="section-title">Barriers, Behaviors, and Supervision Needs</h5>
          <el-input
            v-model="report.paragraph_two"
            type="textarea"
            :rows="8"
            placeholder="AI-generated narrative about barriers, behaviors, and supervision..."
          />
          <div class="char-count">{{ (report.paragraph_two || '').length }} characters</div>
        </div>

        <!-- Per-Code Summaries -->
        <div v-if="perCodeEntries.length > 1" class="report-section">
          <h5 class="section-title" @click="showPerCode = !showPerCode" style="cursor: pointer">
            Per-Code Summaries
            <i :class="showPerCode ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="font-size: 12px; margin-left: 6px"></i>
          </h5>
          <div v-if="showPerCode">
            <div v-for="[code, summary] in perCodeEntries" :key="code" class="per-code-item">
              <label class="per-code-label">{{ code }}</label>
              <el-input
                :value="summary"
                @input="updatePerCodeSummary(code, $event)"
                type="textarea"
                :rows="3"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Action Buttons -->
      <div class="report-actions">
        <template v-if="!report.uploaded_file_path">
          <el-button type="primary" @click="saveReport" :loading="saving">
            <i class="fa fa-save" style="margin-right: 6px"></i> Save Draft
          </el-button>
          <el-button type="success" @click="saveAsFinal" :loading="saving">
            <i class="fa fa-check" style="margin-right: 6px"></i> Save as Final
          </el-button>
          <el-button type="warning" @click="regenerateReport" :loading="generating">
            <i class="fa fa-redo" style="margin-right: 6px"></i> Regenerate
          </el-button>
        </template>
        <el-button type="info" @click="downloadPdf" :loading="downloading">
          <i class="fa fa-file-pdf" style="margin-right: 6px"></i> Download PDF
        </el-button>
        <el-button
          v-if="report.status === 'final'"
          type="primary"
          @click="openEmailModal"
        >
          <i class="fa fa-envelope" style="margin-right: 6px"></i> Email to Support Coordinator
        </el-button>
      </div>
    </div>

    <!-- Email Modal -->
    <AIVDialog
      v-model="emailModalVisible"
      title="Email Quarterly Report to Support Coordinator"
      width="600px"
      height="80vh"
    >
      <el-form label-position="top" class="full-width-form">
        <AIVRow
          :record.sync="emailForm"
          :fields="[
            { label: 'To', key: 'to', placeholder: 'Recipient email' },
          ]"
        />
        <AIVRow
          :record.sync="emailForm"
          :fields="[
            { label: 'Subject', key: 'subject' },
          ]"
        />
        <div class="row mb-3 w-100 mx-0">
          <div class="col-12 px-2">
            <label class="d-block">Body</label>
            <el-input
              v-model="emailForm.body"
              type="textarea"
              :rows="8"
            ></el-input>
          </div>
        </div>
        <div class="row mb-3 w-100 mx-0">
          <div class="col-12 px-2">
            <div style="color: #999; font-size: 12px;">
              <i class="fa fa-paperclip" style="margin-right: 4px"></i>
              Attached: Quarterly_Report_{{ clientDisplayName.replace(' ', '_') }}_{{ report ? report.start_date : '' }}_{{ report ? report.end_date : '' }}.pdf
            </div>
          </div>
        </div>
      </el-form>
      <template slot="footer">
        <div class="text-center">
          <el-button round @click="emailModalVisible = false">Cancel</el-button>
          <el-button type="primary" round @click="sendEmail" :loading="emailing">
            <i class="fa fa-paper-plane" style="margin-right: 6px"></i> Send Email
          </el-button>
        </div>
      </template>
    </AIVDialog>

    <!-- Section 3: Report History -->
    <div v-if="selectedClientId && reportHistory.length > 0" class="history-container">
      <h5 class="section-title" @click="showHistory = !showHistory" style="cursor: pointer">
        Previous Reports ({{ reportHistory.length }})
        <i :class="showHistory ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="font-size: 12px; margin-left: 6px"></i>
      </h5>
      <div v-if="showHistory">
        <div
          v-for="hist in reportHistory"
          :key="hist.id"
          class="history-item"
          @click="loadReport(hist.id)"
        >
          <span class="history-date">{{ hist.start_date }} - {{ hist.end_date }}</span>
          <span :class="['status-badge', hist.status]">{{ hist.status }}</span>
          <span v-if="hist.uploaded_file_path" class="status-badge uploaded">uploaded</span>
          <span class="history-codes">{{ parseServiceCodes(hist.service_codes) }}</span>
          <el-button
            type="danger"
            size="mini"
            icon="fa fa-trash"
            @click.stop="deleteReport(hist.id)"
          ></el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { quarterlyReportsService } from "src/serviceHandlers/quarterlyReportsService";
import { clientsService } from "src/serviceHandlers/clientsService";

// Separate codes that should NOT be combined with others
const SEPARATE_CODES = ["PBA", "HHS", "ELS"];

export default {
  name: "QuarterlyReport",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    externalClientId: {
      type: [Number, String],
      default: null,
    },
    externalDateRange: {
      type: Array,
      default: null,
    },
    externalServiceCodes: {
      type: Array,
      default: () => [],
    },
    triggerGenerate: {
      type: Number,
      default: 0,
    },
    triggerUpload: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // Form
      selectedClientId: null,
      dateRange: null,
      selectedServiceCodes: [],
      serviceCodes: [],
      clients: [],

      // Report
      report: null,
      reportHistory: [],

      // UI state
      generating: false,
      saving: false,
      downloading: false,
      uploading: false,
      showPerCode: true,
      showHistory: false,

      // Email modal
      emailModalVisible: false,
      emailing: false,
      emailForm: {
        to: "",
        subject: "",
        body: "",
      },
    };
  },
  computed: {
    clientOptions() {
      return this.clients.map((c) => ({
        name: `${c.first_name} ${c.last_name}`,
        id: c.id,
      }));
    },
    serviceCodeOptions() {
      return this.serviceCodes.map((sc) => ({
        name: `${sc.code} - ${sc.name}`,
        shortName: sc.code,
        id: sc.code,
      }));
    },
    canGenerate() {
      return (
        this.selectedClientId &&
        this.dateRange &&
        this.dateRange.length === 2 &&
        this.selectedServiceCodes.length > 0 &&
        !this.generating
      );
    },
    canUpload() {
      return (
        this.selectedClientId &&
        this.dateRange &&
        this.dateRange.length === 2 &&
        !this.uploading
      );
    },
    clientDisplayName() {
      if (!this.report || !this.report.client) return "";
      return `${this.report.client.first_name} ${this.report.client.last_name}`;
    },
    creatorDisplayName() {
      if (!this.report || !this.report.createdBy) return "";
      return `${this.report.createdBy.first_name} ${this.report.createdBy.last_name}`;
    },
    displayServiceCodes() {
      try {
        const codes = JSON.parse(this.report.service_codes || "[]");
        return codes.join(", ");
      } catch {
        return "";
      }
    },
    perCodeEntries() {
      try {
        const obj = JSON.parse(this.report.per_code_summaries || "{}");
        return Object.entries(obj);
      } catch {
        return [];
      }
    },
    quarterButtons() {
      const now = new Date();
      const year = now.getFullYear();
      const prevYear = year - 1;
      return [
        { label: `Q1 ${year}`, start: `${year}-01-01`, end: `${year}-03-31` },
        { label: `Q2 ${year}`, start: `${year}-04-01`, end: `${year}-06-30` },
        { label: `Q3 ${year}`, start: `${year}-07-01`, end: `${year}-09-30` },
        { label: `Q4 ${year}`, start: `${year}-10-01`, end: `${year}-12-31` },
        { label: `Q1 ${prevYear}`, start: `${prevYear}-01-01`, end: `${prevYear}-03-31` },
        { label: `Q2 ${prevYear}`, start: `${prevYear}-04-01`, end: `${prevYear}-06-30` },
        { label: `Q3 ${prevYear}`, start: `${prevYear}-07-01`, end: `${prevYear}-09-30` },
        { label: `Q4 ${prevYear}`, start: `${prevYear}-10-01`, end: `${prevYear}-12-31` },
      ];
    },
    codeWarning() {
      if (!this.selectedServiceCodes || this.selectedServiceCodes.length < 2) return "";
      const hasSeparate = this.selectedServiceCodes.some((c) => SEPARATE_CODES.includes(c));
      const hasCombinable = this.selectedServiceCodes.some((c) => !SEPARATE_CODES.includes(c));
      if (hasSeparate && hasCombinable) {
        return "PBA, HHS, and ELS typically require separate quarterly reports from other codes.";
      }
      return "";
    },
  },
  watch: {
    externalClientId: {
      immediate: true,
      async handler(val) {
        if (this.embedded && val) {
          this.selectedClientId = val;
          await this.onClientChange();
        }
      },
    },
    externalDateRange: {
      immediate: true,
      async handler(val) {
        if (this.embedded && val && val.length === 2) {
          this.dateRange = val;
          await this.onDateChange();
        }
      },
    },
    externalServiceCodes: {
      handler(val) {
        if (this.embedded) {
          this.selectedServiceCodes = val || [];
        }
      },
    },
    triggerGenerate: {
      handler(val) {
        if (this.embedded && val > 0 && this.canGenerate) {
          this.generateReport();
        }
      },
    },
    triggerUpload: {
      handler(val) {
        if (this.embedded && val > 0 && this.canUpload) {
          this.triggerUploadFile();
        }
      },
    },
  },
  async mounted() {
    await this.loadClients();
    // In embedded mode, apply initial props after clients are loaded
    if (this.embedded) {
      if (this.externalClientId) {
        this.selectedClientId = this.externalClientId;
        await this.onClientChange();
      }
      if (this.externalDateRange && this.externalDateRange.length === 2) {
        this.dateRange = this.externalDateRange;
        await this.onDateChange();
      }
    }
  },
  methods: {
    async loadClients() {
      try {
        const data = await clientsService.list({ payer_type: "DSPD", active: true, slim: true });
        this.clients = Array.isArray(data) ? data : data.rows || [];
      } catch (err) {
        this.$notifyError("Failed to load clients");
      }
    },

    async onClientChange() {
      this.serviceCodes = [];
      this.selectedServiceCodes = [];
      this.report = null;
      if (this.selectedClientId) {
        await this.loadHistory();
        if (this.dateRange && this.dateRange.length === 2) {
          await this.loadServiceCodes();
        }
      }
    },

    async onDateChange() {
      this.serviceCodes = [];
      this.selectedServiceCodes = [];
      if (this.selectedClientId && this.dateRange && this.dateRange.length === 2) {
        await this.loadServiceCodes();
      }
    },

    setQuarter(q) {
      this.dateRange = [q.start, q.end];
      this.onDateChange();
    },

    isActiveQuarter(q) {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      return this.dateRange[0] === q.start && this.dateRange[1] === q.end;
    },

    async loadServiceCodes() {
      try {
        const codes = await quarterlyReportsService.getClientServiceCodes(
          this.selectedClientId,
          { startDate: this.dateRange[0], endDate: this.dateRange[1] }
        );
        this.serviceCodes = codes || [];
        if (this.embedded) {
          this.$emit('service-codes-loaded', this.serviceCodeOptions);
        }
      } catch (err) {
        this.$notifyError("Failed to load service codes");
      }
    },

    async loadHistory() {
      try {
        this.reportHistory = await quarterlyReportsService.getQuarterlyReportsForClient(
          this.selectedClientId
        );
      } catch {
        this.reportHistory = [];
      }
    },

    async generateReport() {
      if (!this.canGenerate) return;
      this.generating = true;
      this.$emit('generating', true);
      try {
        const result = await quarterlyReportsService.generateQuarterlyReport({
          clientId: this.selectedClientId,
          startDate: this.dateRange[0],
          endDate: this.dateRange[1],
          serviceCodes: this.selectedServiceCodes,
        });
        this.report = result;
        this.$notifySuccess("Report generated successfully");
        await this.loadHistory();
      } catch (err) {
        this.$notifyError("Failed to generate report: " + (err.message || "Unknown error"));
      } finally {
        this.generating = false;
        this.$emit('generating', false);
      }
    },

    triggerUploadFile() {
      if (!this.canUpload) return;
      this.$refs.uploadInput.value = "";
      this.$refs.uploadInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploading = true;
      this.$emit('uploading', true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("clientId", this.selectedClientId);
        formData.append("startDate", this.dateRange[0]);
        formData.append("endDate", this.dateRange[1]);

        const result = await quarterlyReportsService.uploadQuarterlyReport(formData);
        this.report = result;
        this.$notifySuccess("Report uploaded and finalized successfully");
        await this.loadHistory();
      } catch (err) {
        this.$notifyError("Failed to upload report: " + (err.message || "Unknown error"));
      } finally {
        this.uploading = false;
        this.$emit('uploading', false);
      }
    },

    async regenerateReport() {
      if (!this.report) return;
      // Delete the current report and regenerate
      if (this.report.id) {
        try {
          await quarterlyReportsService.deleteQuarterlyReport(this.report.id);
        } catch {
          // Ignore delete errors
        }
      }
      await this.generateReport();
    },

    async saveReport() {
      if (!this.report || !this.report.id) return;
      this.saving = true;
      try {
        const updated = await quarterlyReportsService.updateQuarterlyReport(this.report.id, {
          goals_text: this.report.goals_text,
          supports_text: this.report.supports_text,
          paragraph_one: this.report.paragraph_one,
          paragraph_two: this.report.paragraph_two,
          per_code_summaries: this.report.per_code_summaries,
          status: "draft",
        });
        this.report = updated;
        this.$notifySuccess("Report saved");
        await this.loadHistory();
      } catch (err) {
        this.$notifyError("Failed to save report");
      } finally {
        this.saving = false;
      }
    },

    async saveAsFinal() {
      if (!this.report || !this.report.id) return;
      this.saving = true;
      try {
        const updated = await quarterlyReportsService.updateQuarterlyReport(this.report.id, {
          goals_text: this.report.goals_text,
          supports_text: this.report.supports_text,
          paragraph_one: this.report.paragraph_one,
          paragraph_two: this.report.paragraph_two,
          per_code_summaries: this.report.per_code_summaries,
          status: "final",
        });
        this.report = updated;
        this.$notifySuccess("Report finalized");
        await this.loadHistory();
      } catch (err) {
        this.$notifyError("Failed to finalize report");
      } finally {
        this.saving = false;
      }
    },

    async downloadPdf() {
      if (!this.report || !this.report.id) return;
      this.downloading = true;
      try {
        const data = await quarterlyReportsService.downloadQuarterlyReportPdf(this.report.id);
        const blob = new Blob([data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Quarterly_Report_${this.report.start_date}_${this.report.end_date}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.$notifyError("Failed to download PDF");
      } finally {
        this.downloading = false;
      }
    },

    async loadReport(id) {
      try {
        this.report = await quarterlyReportsService.getQuarterlyReport(id);
      } catch (err) {
        this.$notifyError("Failed to load report");
      }
    },

    async deleteReport(id) {
      try {
        await this.$confirmAction(async () => {
          await quarterlyReportsService.deleteQuarterlyReport(id);
          if (this.report && this.report.id === id) {
            this.report = null;
          }
          await this.loadHistory();
          this.$notifySuccess("Report deleted");
        }, "Delete this quarterly report?");
      } catch {
        // Cancelled
      }
    },

    parseServiceCodes(json) {
      try {
        return JSON.parse(json || "[]").join(", ");
      } catch {
        return "";
      }
    },

    updatePerCodeSummary(code, value) {
      try {
        const obj = JSON.parse(this.report.per_code_summaries || "{}");
        obj[code] = value;
        this.report.per_code_summaries = JSON.stringify(obj);
      } catch {
        // ignore
      }
    },

    openEmailModal() {
      if (!this.report) return;
      const client = this.report.client || {};
      const sc = client.support_coordinator;
      const clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim();

      this.emailForm.to = sc ? sc.email || "" : "";
      this.emailForm.subject = `Quarterly Report for ${clientName} (${this.report.start_date} to ${this.report.end_date})`;
      this.emailForm.body =
        `Hello${sc ? ` ${sc.first_name}` : ""},\n\n` +
        `Please find attached the quarterly report for ${clientName} covering the period ${this.report.start_date} to ${this.report.end_date}.\n\n` +
        `Please let us know if you have any questions or need additional information.`;
      this.emailModalVisible = true;
    },

    async sendEmail() {
      if (!this.emailForm.to) {
        this.$notifyError("Recipient email is required");
        return;
      }
      this.emailing = true;
      try {
        const result = await quarterlyReportsService.emailQuarterlyReport(this.report.id, {
          to: this.emailForm.to,
          subject: this.emailForm.subject,
          body: this.emailForm.body,
        });
        if (result.report) {
          this.report = result.report;
        }
        this.$notifySuccess("Email sent successfully");
        this.emailModalVisible = false;
        await this.loadHistory();
      } catch (err) {
        this.$notifyError("Failed to send email: " + (err.message || "Unknown error"));
      } finally {
        this.emailing = false;
      }
    },
  },
};
</script>

<style scoped>
.filters-container {
  padding: 15px;
  margin-bottom: 15px;
  border-radius: 8px;
  background-color: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-group-compact {
  display: flex;
  align-items: center;
}

.compact-select {
  min-width: 220px;
}

.quarter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.code-warning {
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(230, 162, 60, 0.2);
  border: 1px solid rgba(230, 162, 60, 0.5);
  border-radius: 4px;
  color: #e6a23c;
  font-size: 13px;
}

.report-container {
  margin-top: 15px;
}

.report-header-card {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 15px;
}

.report-header-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px 30px;
}

.header-field {
  display: flex;
  align-items: center;
  gap: 6px;
}

.header-label {
  color: #999;
  font-size: 13px;
  font-weight: 600;
}

.header-value {
  color: #e0e0e0;
  font-size: 13px;
}

.report-section {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 12px;
}

.section-title {
  color: #e0e0e0;
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
}

.char-count {
  text-align: right;
  color: #777;
  font-size: 11px;
  margin-top: 4px;
}

.per-code-item {
  margin-bottom: 10px;
}

.per-code-label {
  display: block;
  color: #b0b0b0;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 4px;
}

.report-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 15px;
  margin-bottom: 20px;
}

.history-container {
  padding: 15px 20px;
  border-radius: 8px;
  background-color: rgba(37, 55, 65, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 15px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
  margin-bottom: 4px;
}

.history-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.history-date {
  color: #e0e0e0;
  font-size: 13px;
  min-width: 180px;
}

.history-codes {
  color: #999;
  font-size: 12px;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.draft {
  background: rgba(230, 162, 60, 0.2);
  color: #e6a23c;
}

.status-badge.final {
  background: rgba(103, 194, 58, 0.2);
  color: #67c23a;
}

.status-badge.uploaded {
  background: rgba(64, 158, 255, 0.2);
  color: #409eff;
}
</style>
